import { useOutletContext } from 'react-router-dom';

import AnalyticsTable from '../../../components/AiSpend/AnalyticsTable';
import BarChart from '../../../components/Charts/BarChart';
import ChartCard from '../../../components/Charts/ChartCard';
import ErrorState from '../../../components/DataDisplay/ErrorState';
import StatCard from '../../../components/DataDisplay/StatCard';
import { getAiSpendDaily } from '../../../services/aiSpendService';
import { formatCount, formatMoney } from '../../../utils/helpers/aiSpendFormat';
import useApiResource from '../../../utils/hooks/useApiResource';

export default function AiSpendDaily() {
  const { queryParams, queryKey } = useOutletContext();
  const { data, error, loading, refetch } = useApiResource(
    ({ signal }) => getAiSpendDaily({ params: queryParams, signal }),
    { deps: [queryKey] },
  );

  if (error) return <ErrorState message={error} onRetry={refetch} />;

  const target = data?.soft_target_usd;
  const days = data?.daily || [];

  return (
    <div className="space-y-6">
      <p className="rounded-xl border border-slate-200 bg-white px-4 py-3 text-sm text-slate-600 shadow-sm dark:border-slate-700 dark:bg-slate-800 dark:text-slate-300">
        Observation only — nothing is blocked when a day exceeds the target.
      </p>

      <section className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
        <StatCard
          label="Daily target"
          value={target ? formatMoney(target) : 'Not set'}
          rawValue={target}
          detail="Display only"
          loading={loading}
        />
        <StatCard
          label="Average per day"
          value={formatMoney(data?.avg_day_cost_usd)}
          rawValue={data?.avg_day_cost_usd}
          loading={loading}
        />
        <StatCard
          label="Most expensive day"
          value={formatMoney(data?.max_day_cost_usd)}
          rawValue={data?.max_day_cost_usd}
          loading={loading}
        />
        <StatCard
          label="Days over target"
          value={formatCount(data?.days_over_target)}
          rawValue={data?.days_over_target}
          tone={data?.days_over_target ? 'amber' : 'slate'}
          loading={loading}
        />
      </section>

      <ChartCard
        title="Daily spend against the target"
        subtitle="The target line is a reference, not a limit"
        loading={loading}
        isEmpty={!loading && days.every((day) => !day.cost_usd)}
      >
        <BarChart
          ariaLabel="Daily spend against target"
          labels={days.map((day) => day.label)}
          datasets={[
            {
              key: 'cost',
              label: 'Cost',
              data: days.map((day) => day.cost_usd || 0),
              color: '#2C59F8',
            },
            ...(target
              ? [
                  {
                    key: 'target',
                    label: 'Target',
                    type: 'line',
                    color: '#F45B2B',
                    data: days.map(() => target),
                  },
                ]
              : []),
          ]}
        />
      </ChartCard>

      <AnalyticsTable
        loading={loading}
        rows={days}
        rowKey={(row) => row.date}
        columns={[
          { key: 'label', label: 'Day', render: (row) => row.label },
          {
            key: 'cost_usd',
            label: 'Cost',
            render: (row) => (
              <span
                className={
                  target && row.cost_usd > target
                    ? 'font-semibold text-orange-600 dark:text-orange-400'
                    : 'font-semibold'
                }
              >
                {formatMoney(row.cost_usd)}
              </span>
            ),
          },
          { key: 'calls', label: 'Calls', render: (row) => formatCount(row.calls) },
          {
            key: 'failed_calls',
            label: 'Failed',
            render: (row) => formatCount(row.failed_calls),
          },
        ]}
      />
    </div>
  );
}
